/**
 * Which files a single Redact All press would actually burn (VE-31006).
 *
 * DERIVED, NEVER QUEUED. The list is computed from the rows in hand at the
 * moment the button is pressed and nothing about it is persisted, which buys
 * two properties outright. It is idempotent — run it twice and the second
 * run's list is whatever is still behind. And it is resumable for free: the
 * burn is browser-side, so a run dies with the tab, and dying at file 12 of 20
 * costs only the time. Re-open, press again, and the list is the remaining 8.
 * No job record, no partial state to reconcile. A queue persisted anywhere
 * would have to be reconciled against the assets regardless, because the
 * assets are the truth.
 *
 * TWO SCOPES, ONE RULE. With a merged document the files are its members;
 * without one they are the request's documents. Only the membership source
 * changes — what makes a file WORK is the same question either way, which is
 * why this takes rows and a member set rather than knowing about projects.
 *
 * A THIRD SCOPE, SUBTRACTED AFTER THE FACT (VE-31538). A press carrying
 * members re-burns each of them and rebuilds the merged PDF, so when the set
 * is not this session's to write, `withoutMergeSetMembers` takes them back
 * out and the press covers only the files outside it. Expressed as a
 * narrowing of the finished list rather than a condition inside the build,
 * because everything the button says is read off the list — narrow it and
 * the label, the count and the act narrow together.
 *
 * A FILE NOBODY HAS WORKED ON IS NEVER IN THE LIST, and the reason is a
 * product rule rather than a technical limit. Burning a file with no saved
 * redaction data publishes a PDF identical to the original and stamps it as a
 * redacted output — the system asserting "reviewed, nothing needed" about a
 * file nobody has looked at. The act being skipped is the looking, not the
 * clicking. Those files are the existing modal's business: review them, drop
 * them from the set, or include them deliberately.
 *
 * It matters MORE without a merged document, not less: a bare Redact All over
 * a whole request could otherwise publish "reviewed, nothing needed" across
 * every untouched file in one press. `skippedUnworked` is counted rather than
 * discarded so a caller can say how many were passed over, which is what a
 * confirmation step would be built on.
 *
 * THE SAME RULE DECIDES WHAT A MERGE MAY LEAVE OUT, so it lives here too:
 * `neverWorkedOnMembers` (with names, for the combine) and `neverWorkedOnIds`
 * (as ids, for the Bates numbering, where a left-out member spans no
 * numbers — VE-32395). Every caller goes through one of the two. A press
 * whose reviewer chose to include some of them numbers from what IT leaves
 * out instead — the combine's `excludable` list (VE-32440).
 *
 * "WORKED ON" IS READ OFF THE ROWS, and the rows are kept honest at the
 * source: the editor patches the file's row when it saves
 * (`redactionDataSaved`), the same way it already does when it burns one. So
 * this needs no second opinion about what the editor has been doing — a
 * threaded session override was tried here and removed, because it could not
 * survive a document switch (a saved session is discarded, not parked).
 */
import {
  collectBatesBehindTdoIds,
  collectStaleOutputTdoIds,
  collectWatermarksBehindTdoIds,
  isFileRedactionRunning,
  pickNewestOutput,
} from './staleness';
import { readAssetIncludedUnreviewed } from '@redact-modules/documents/helpers';
import { isMachineWrittenRedactionAsset } from '@redact-modules/documents/redactionAssetWriter';
import { fileHasRedactedOutput } from './redactedOutput';
import { isRosterOnlyRow } from './rosterRow';
import type { AssetFileType } from './store';

/**
 * Why a file is in the list. Several can apply at once — a member can be
 * behind its own saves AND behind the set's numbering — and the modal names
 * them, so they are kept rather than collapsed to a boolean.
 */
export type RedactAllReason =
  /** Saved redactions the newest output does not contain. */
  | 'missingLatestEdits'
  /**
   * The output's numbering does not match the merged document's.
   *
   * EITHER DIRECTION. Missing the set's current numbers, or still carrying
   * numbers the set no longer uses — a file that left the set, or one whose
   * set stopped stamping. One reason rather than two because the reviewer is
   * being told what the press will do, and it does the same thing to both.
   */
  | 'batesBehind'
  /** The output does not carry the merged document's current watermarks. */
  | 'watermarksBehind'
  /**
   * Worked on and saved, but never burned at all.
   *
   * INVISIBLE TO THE STALENESS VERDICT, which answers "is this output behind?"
   * and returns `not stale` when there is no output to be behind. That is
   * right for a folder badge — nothing has been released, so nothing is
   * misleading anybody — and wrong here, because the merge REFUSES over
   * exactly these files ("1 file has not been redacted yet"). Leaving them out
   * would make Redact All & Merge burn everything else and then still fail.
   */
  | 'neverBurned'
  /**
   * Edits the reviewer has not saved yet.
   *
   * ALSO INVISIBLE TO THE STALENESS VERDICT, and for a better reason than
   * `neverBurned`: that verdict compares SAVED redactions against the output,
   * and these are not saved, so nothing it can see has changed. The file is
   * still behind what the reviewer decided — the decision is simply still in
   * the editor.
   *
   * The pass saves it before burning, which is what PART 2 meant by "save
   * them, don't block": `sessionFor` hands the live or suspended session to
   * the burn, and `saveBeforeExport` persists whatever snapshot it is given.
   * Leaving these out made the button vanish for the commonest case there is
   * — one file edited, nothing else wrong.
   */
  | 'unsaved'
  /**
   * NOBODY HAS WORKED ON IT AND THE REVIEWER SAID INCLUDE IT ANYWAY.
   *
   * The one reason that is not a complaint about the file. Every other entry
   * here is in the list because something is out of date; this one is in it
   * because it was asked for — see `includeUnworkedTdoIds`, and the rule it
   * is the deliberate exception to in this file's header.
   *
   * It is also the reason the BURN has to read: an unworked file has no
   * session to build from, so `buildHeadlessSession` refuses it unless the
   * caller says this entry is one of these.
   */
  | 'includedUnworked';

export interface RedactAllEntry {
  readonly tdoId: string;
  readonly name: string | null;
  readonly reasons: readonly RedactAllReason[];
  /** In the merged document, so burning it invalidates the merged PDF. */
  readonly isMember: boolean;
}

export interface RedactAllWorkList {
  readonly entries: readonly RedactAllEntry[];
  /**
   * Does this run touch the merge set? NOT "is there a merged document" — a
   * request can hold stale files on both sides of one, and a run that touches
   * none of its members has no reason to rebuild it.
   */
  readonly touchesMergeSet: boolean;
  /** Files passed over because nobody has worked on them. */
  readonly skippedUnworked: number;
  /** Files passed over because a burn is already running on them. */
  readonly skippedRunning: number;
  /**
   * Files that were WORKED ON AND NEEDED NOTHING — already redacted, and
   * current (VE-31796).
   *
   * The ids rather than a count, because the two callers want the number
   * over different scopes: the footer's Redact all is about the request, and
   * the merged document's Update is about its members. A count here would
   * answer only the first, and the second would have to re-derive it by
   * subtracting three other numbers.
   *
   * The progress modal needs it because "Redacting 2 of 2" over a
   * twenty-file request reads as a pass that has lost eighteen files.
   */
  readonly currentIds: ReadonlySet<string>;
}

/**
 * Has a PERSON saved redactions for this file?
 *
 * Not "does a redaction asset exist". The lexicon step in the upload dialog
 * writes one before anybody opens the file, and reading that as work is how
 * the merge came to refuse over documents nobody had touched (see
 * `serviceResolveMemberOutputs`). The editor's open path and
 * `watermarkAutoApply` draw the line in the same place; all four readers are
 * looking at the same files and have to agree.
 *
 * The row carries only the NEWEST such asset, which is enough: the upload stub
 * is written at ingestion, so a save always lands after it. A stub that is
 * still the newest means nothing has been saved since the upload.
 */
export const hasReviewerSavedRedactions = (
  file: AssetFileType | null | undefined
): boolean => {
  const newest = file?.redactionDataAssets?.records?.[0];
  return !!newest && !isMachineWrittenRedactionAsset(newest.details);
};

/**
 * The members that still EXIST in the request — trashed and deleted ones
 * dropped.
 *
 * A MEMBER LIST IS NOT A FILE LIST. Membership is recorded on the project and
 * outlives the files in it, so a request whose documents have been deleted
 * keeps naming them as members. Everything the merge actually does is over the
 * ones that are still there, which makes this the honest denominator for
 * "how many files would this build have?" — counting the roster instead
 * overstates it, and the place that hurts is `useUnworkedPrompt.shortfall`,
 * where the overstatement hides a merge the combine is about to refuse for
 * having fewer than two files.
 *
 * AN UNREAD ROSTER IS NOT AN EMPTY ONE. With no files read yet every member
 * would look deleted, so the ids pass through untouched — the same way the
 * controls built on it stay inert rather than scoped to nothing until the
 * request lands.
 */
export const reachableMembers = <
  T extends string | { toString(): string },
>(args: {
  readonly files: readonly (AssetFileType | null | undefined)[];
  readonly memberIds: readonly T[] | null | undefined;
}): readonly T[] => {
  const { files, memberIds } = args;
  const ids = memberIds ?? [];
  if (files.length === 0) {
    return ids;
  }
  const present = new Set(
    files
      .filter((file): file is AssetFileType => !!file)
      .map((file) => String(file.id))
  );
  return ids.filter((id) => present.has(String(id)));
};

/**
 * The members a merge may LEAVE OUT rather than refuse over: files nobody
 * has opened — no released output, no saved redactions, nothing in flight in
 * the browser (VE-31796).
 *
 * ONE COPY. This predicate decides what a release is allowed to omit, which
 * makes it the most consequential three-line test in the editor, and it had
 * been written out four times — the panel, the editor view, the merge-update
 * hook, and the merge's own member classifier. The view's copy carried a
 * comment calling itself "sixth and last copy of the question", which is a
 * fair summary of how that goes.
 *
 * IMPORT IT UNDER A DISTINCT NAME where a local holds its result. The
 * panel's extraction kept the old name for both, and `.length` on a
 * function is legal TypeScript — it read the arity, 1, and the panel spent a
 * commit telling every reviewer that exactly one file had been left out of
 * their release. `tsc` cannot see that; an alias makes it a compile error.
 * It applies to `reachableMembers` above too, which both of its callers read
 * a `.length` off.
 *
 * NAMES TRAVEL WITH IT, because the combine records the exclusion on the
 * asset and only this side can see them. No name filter: a member whose name
 * has not arrived is nameless, not unexcludable — dropping it made the
 * combine refuse over a file the banner had promised would simply be left
 * out.
 */
export const neverWorkedOnMembers = (args: {
  readonly files: readonly (AssetFileType | null | undefined)[];
  readonly memberIds:
    | readonly (string | { toString(): string })[]
    | null
    | undefined;
  /** Members with edits held only in the browser: plainly worked on. */
  readonly unsavedTdoIds: ReadonlySet<string>;
}): readonly { tdoId: AssetFileType['id']; name: string }[] => {
  const { files, memberIds, unsavedTdoIds } = args;
  const byId = new Map(
    files
      .filter((file): file is AssetFileType => !!file)
      .map((file) => [String(file.id), file])
  );
  return (memberIds ?? [])
    .map((id) => byId.get(String(id)))
    .filter(
      (file): file is AssetFileType =>
        !!file &&
        // Through the roster's boolean too: a row the heavy query has not
        // reached carries no asset list, and reading that as "no output"
        // listed released members as left out (VE-32420).
        !fileHasRedactedOutput(file) &&
        // NOT "has no redaction asset": the upload dialog's lexicon step
        // writes one before anybody opens the file, and reading that as work
        // is how the merge came to refuse over documents nobody had touched.
        !hasReviewerSavedRedactions(file) &&
        !unsavedTdoIds.has(String(file.id))
    )
    .map((file) => ({ tdoId: file.id, name: file.name ?? '' }));
};

/**
 * A ROSTER ROW (VE-32420): the heavy query has not reached it, so it carries
 * no redaction data, and the predicate above reads that as untouched.
 *
 * FAILS CLOSED FOR THE NUMBERING ONLY. Such a member still spans its numbers,
 * because a member wrongly spanning only leaves a gap in the release, while
 * one wrongly NOT spanning puts its numbers on the member after it too. The
 * merge's own list is left as it was: the combine re-reads the assets live
 * and refuses over a member that turns out to have been worked on.
 */
const isRedactionDataUnread = (
  file: AssetFileType | null | undefined
): boolean => isRosterOnlyRow(file) && file?.redactionDataAssets === undefined;

/**
 * The members of a merge's left-out list that span no Bates numbers
 * (VE-32395; see `effectiveBatesStarts`): all of them, less any whose
 * redaction data has not been read (above).
 *
 * TAKES THE LIST rather than working it out, because a press can change it:
 * a reviewer who includes files nobody has worked on (VE-32440) hands the
 * combine a shorter list, and the numbering has to follow THAT list, or the
 * included files burn over the numbers of the members after them.
 */
export const batesExcludedIds = (
  files: readonly (AssetFileType | null | undefined)[],
  leftOut: readonly { readonly tdoId: string | { toString(): string } }[]
): ReadonlySet<string> => {
  const unread = new Set(
    files.filter(isRedactionDataUnread).map((file) => String(file!.id))
  );
  return new Set(
    leftOut
      .map((member) => String(member.tdoId))
      .filter((id) => !unread.has(id))
  );
};

/**
 * The same, for a merge that leaves out every member nobody has worked on —
 * the shape the numbering callers outside a press want. One copy, so the
 * numbering cannot drift from what the merge leaves out.
 */
export const neverWorkedOnIds = (
  args: Parameters<typeof neverWorkedOnMembers>[0]
): ReadonlySet<string> =>
  batesExcludedIds(args.files, neverWorkedOnMembers(args));

const hasOutput = (file: AssetFileType): boolean =>
  (file.redactedMediaAssets?.records?.length ?? 0) > 0;

/**
 * Was this file's newest output released WITHOUT anybody reviewing it
 * (VE-32440)?
 *
 * The reviewer's decision, read back off the artifact it produced. That is
 * the only place it survives — the editor's own record of it is a dialog
 * answer scoped to one visit — and it is what tells a LATER burn of the same
 * file that it is re-releasing an unreviewed document rather than making a
 * fresh decision about it.
 *
 * THE NEWEST, through the same picker the staleness checks use. Reading
 * `records[0]` would have read whichever the query happened to return first,
 * and a file re-burned after a genuine review must stop being unreviewed.
 */
export const isUnreviewedRelease = (file: AssetFileType): boolean =>
  readAssetIncludedUnreviewed(pickNewestOutput(file)?.details);

/**
 * The files in this list whose released output nobody reviewed — the row
 * mark's set, built the same way the work list builds its own answer.
 *
 * A HUMAN SAVE CLEARS IT, which is why this is not simply a read of the
 * output: the marker records what the LAST burn released, and a reviewer who
 * has since opened the file and saved has answered the question the mark
 * exists to ask. Same pairing as `releasedUnreviewed` inside the builder.
 */
export const collectUnreviewedReleaseTdoIds = (
  files: readonly (AssetFileType | null | undefined)[]
): ReadonlySet<string> =>
  new Set(
    files
      .filter(
        (file): file is AssetFileType =>
          !!file &&
          !hasReviewerSavedRedactions(file) &&
          isUnreviewedRelease(file)
      )
      .map((file) => String(file.id))
  );

export const buildRedactAllWorkList = (args: {
  readonly files: readonly (AssetFileType | null | undefined)[];
  readonly redactEngineId: string | undefined;
  /**
   * The merged document's members, or `undefined` when there is no merged
   * document OR its membership has not been read.
   *
   * Those two are deliberately the same answer HERE, where they lead to the
   * same place — no member reasons, no merge step — unlike the collectors
   * below, which must not report a member stale on a membership they have not
   * seen. A run started before the membership lands simply does less.
   */
  readonly members: ReadonlySet<string> | undefined;
  readonly projectBatesModifiedDateTime: string | null | undefined;
  readonly expectedStartById?: ReadonlyMap<string, number | null>;
  readonly projectWatermarkModifiedDateTime: string | null | undefined;
  /**
   * What the EDITOR's own sessions say about themselves, by tdo id.
   *
   * The rows come from the folder query and are a snapshot: a document the
   * reviewer has been editing in this visit can have saves the row has never
   * heard of, and the one most likely to be stale is the one in front of them.
   * The Documents panel's badge already folds this in for the same reason, and
   * the two must not disagree — a row marked out of date in the panel and
   * absent from the list this button acts on is the worst kind of quiet.
   *
   * It OVERRIDES in both directions. `true` adds a file the rows call current;
   * `false` removes one the rows call stale, because a session that has since
   * been burned knows better than a row fetched before it was.
   */
  readonly sessionStaleness?: ReadonlyMap<string, boolean>;
  /**
   * Files with edits in the editor that have not been saved.
   *
   * Counted as WORK as well as as a reason: a file somebody is actively
   * editing has plainly been looked at, whatever its saved state says, so it
   * passes the never-opened rule on this alone. `neverWorkedOnMembers` has
   * always drawn the line in the same place.
   */
  readonly unsavedTdoIds?: ReadonlySet<string>;
  /**
   * Files nobody has worked on that the reviewer has asked to RELEASE ANYWAY.
   *
   * The deliberate exception to this file's "a file nobody has worked on is
   * never in the list" rule, and it is deliberate in the only way that
   * matters: it arrives from a dialog that names the count and asks. The
   * product rule was never "these files must not be released" — it was that
   * the system must not assert "reviewed, nothing needed" about them on its
   * own. Asked for, it is the reviewer asserting it.
   *
   * They burn with NO redactions, which is the point: a member of a merged
   * set still needs the set's watermarks and numbering on its pages, and
   * only a burn puts them there. Empty by default, so every caller that has
   * not been given a way to ask keeps the old behaviour exactly.
   */
  readonly includeUnworkedTdoIds?: ReadonlySet<string>;
}): RedactAllWorkList => {
  const {
    files,
    redactEngineId,
    members,
    projectBatesModifiedDateTime,
    expectedStartById,
    projectWatermarkModifiedDateTime,
    sessionStaleness,
    unsavedTdoIds,
    includeUnworkedTdoIds,
  } = args;

  // THE SHARED COLLECTORS, not a fourth copy of each rule. Every one of these
  // is also what paints the folder badge and the editor banner, so a file in
  // this list is a file the reviewer has already been shown as out of date —
  // and a file that is not cannot be quietly burned anyway.
  const stale = new Set(collectStaleOutputTdoIds(files, redactEngineId));
  for (const [tdoId, isStale] of sessionStaleness ?? []) {
    if (isStale) {
      stale.add(tdoId);
    } else {
      stale.delete(tdoId);
    }
  }
  const bates = collectBatesBehindTdoIds(
    files,
    projectBatesModifiedDateTime,
    expectedStartById,
    members
  );
  const watermarks = collectWatermarksBehindTdoIds(
    files,
    projectWatermarkModifiedDateTime,
    members
  );

  const entries: RedactAllEntry[] = [];
  const currentIds = new Set<string>();
  let skippedUnworked = 0;
  let skippedRunning = 0;

  for (const file of files) {
    if (!file) {
      continue;
    }
    const id0 = String(file.id);
    const isUnsaved = !!unsavedTdoIds?.has(id0);
    /**
     * NOBODY HAS DECIDED ANYTHING ABOUT THIS FILE — the state this list
     * passes over by default, and releases on request.
     *
     * THE RELEASED OUTPUT IS PART OF THE QUESTION, which it was not, and
     * that cost a release. `neverWorkedOnMembers` has always required one to
     * be absent; this asked only about the redaction data, and the two
     * agreed only because no file could have an output without a reviewer
     * save. The include path made that state permanent — an output plus a
     * machine-written save — and the predicates then disagreed about such a
     * file forever: the dialog stopped offering it (it has an output) while
     * this went on skipping it (no reviewer save). It could not be burned by
     * any press, so a later change to the set's numbering or watermarks left
     * it behind in a release nothing could rebuild, under a snackbar saying
     * it was "still being redacted". One question, asked the same way in
     * both places.
     */
    const noHumanDecision = !hasReviewerSavedRedactions(file) && !isUnsaved;
    const isUnworked = noHumanDecision && !hasOutput(file);
    if (isUnworked && !includeUnworkedTdoIds?.has(id0)) {
      skippedUnworked += 1;
      continue;
    }
    /**
     * Already released WITHOUT review, and being burned again — to pick up
     * the set's new numbering or marks, not to make a fresh disclosure
     * decision.
     *
     * The decision is on the artifact: the output this burn replaces records
     * `includedUnreviewed`. Carrying it forward is what lets such a file
     * re-enter the ordinary staleness path (above) while still burning with
     * an empty plan, still stamping the new output, and still saying so in
     * the audit trail — a re-burn must not quietly upgrade it to reviewed.
     *
     * AND STILL NOBODY'S DECISION, which is the half a first pass at this
     * got wrong. Read off the output alone, a file somebody opened and
     * genuinely redacted AFTER its unreviewed release kept the marker: the
     * next burn consumed their real work and stamped the output "nobody
     * reviewed this", with an audit line to match. The output says what the
     * LAST burn released; `noHumanDecision` says whether that is still
     * true. The two differ from `isUnworked` only in whether an output
     * exists, which is the symmetry they should have had from the start.
     */
    const releasedUnreviewed = noHumanDecision && isUnreviewedRelease(file);
    // A burn already in flight produces this file's next output on its own.
    // The export path refuses a second run on the same TDO anyway; counting it
    // here keeps the list honest about what the press will do, rather than
    // promising a file it will then skip.
    if (isFileRedactionRunning(file, redactEngineId)) {
      skippedRunning += 1;
      continue;
    }
    const id = id0;
    const reasons: RedactAllReason[] = [];
    if (isUnsaved) {
      reasons.push('unsaved');
    }
    if (isUnworked) {
      // ON ITS OWN, and before everything else. `neverBurned` is the same
      // sentence for a file with work behind it, and the burn reads this one
      // to know it may build a session for a document with nothing saved —
      // so a row carrying both would be describing two different files.
      reasons.push('includedUnworked');
    } else if (!hasOutput(file)) {
      // Nothing released yet. Every collector below is about an output being
      // behind, so none of them has an opinion on this file.
      if (!isUnsaved) {
        // An unsaved file with no output is already accounted for: it is
        // going to be saved and burned, and saying so twice tells the modal
        // to list one file under two headings.
        reasons.push('neverBurned');
      }
    } else {
      if (stale.has(id)) {
        reasons.push('missingLatestEdits');
      }
      // BOTH SETS. `behind` is an output missing the set's current numbering;
      // `orphaned` is one still carrying numbering the set no longer uses —
      // because the file left the set, or because the set stopped stamping.
      // Opposite facts, one remedy: burn this file again. The panel has
      // always drawn them with the same mark for exactly that reason, and
      // reading only `behind` here left the row wearing a dashed ring that
      // says "needs redacting" above a button reading "View merged".
      if (bates.behind.has(id) || bates.orphaned.has(id)) {
        reasons.push('batesBehind');
      }
      if (watermarks.has(id)) {
        reasons.push('watermarksBehind');
      }
    }
    if (reasons.length === 0) {
      // Worked on, not running, and nothing owed: the files the pass is
      // silently right to leave alone, and the ones the modal has to count
      // or its own total looks like a loss. An unreviewed release that is
      // current belongs here too — it owes nothing, and re-burning it to say
      // again that nobody read it would be a pass that never ends.
      currentIds.add(id);
      continue;
    }
    /**
     * A QUALIFIER, not a reason to act — which is why it is appended here
     * rather than pushed above. The file already owes something (its
     * numbering, its marks); this says the burn that settles it is still
     * releasing a document nobody has read, so `sessionFor` may build the
     * empty session and the new output carries the same marker as the one
     * it replaces. Pushed with the others it would be a standing reason,
     * and the pass would re-burn a current file on every press.
     *
     * (`isUnworked` cannot also be true here — it requires no output and
     * this requires one — so there is no double-push to guard against.)
     */
    if (releasedUnreviewed) {
      reasons.push('includedUnworked');
    }
    entries.push({
      tdoId: id,
      name: file.name ?? null,
      reasons,
      isMember: !!members?.has(id),
    });
  }

  return {
    entries,
    touchesMergeSet: entries.some((entry) => entry.isMember),
    skippedUnworked,
    skippedRunning,
    currentIds,
  };
};

/**
 * The same list with the merged document's members taken out (VE-31538).
 *
 * WHY THE WHOLE RUN NARROWS, not just the merge at the end of it. A pass
 * carrying members re-burns each one and rotates out its previous audit CSV
 * BEFORE the combine, so dropping only the rebuild would move the release's
 * parts and leave the release itself behind — worse than doing nothing. The
 * members and the merged PDF stand or fall together, because they are one
 * deliverable.
 *
 * WHY NOT REFUSE THE PRESS INSTEAD. A request holds files on both sides of
 * its merged document, and the ones outside it are nobody's release — the
 * same position `membersBehind` already takes. Withholding the set is not a
 * reason to stop a reviewer redacting six unrelated files.
 *
 * NARROWING THE LIST IS THE WHOLE EDIT, deliberately: the label, the count,
 * the footer line and the dispatched entries are all read off it, so they
 * cannot come apart the way a separate "skip the merge" mode would.
 * `touchesMergeSet` goes false on its own once no member is left, which is
 * what drops the `combine` payload.
 *
 * `skippedUnworked` and `skippedRunning` are counts without ids, so they
 * still describe the whole request. They say what the press passed over, and
 * it passed over those either way.
 */
export const withoutMergeSetMembers = (
  list: RedactAllWorkList
): RedactAllWorkList & { readonly withheldMembers: number } => {
  const entries = list.entries.filter((entry) => !entry.isMember);
  const withheldMembers = list.entries.length - entries.length;
  if (withheldMembers === 0) {
    // Nothing to withhold. Returning the list itself keeps the panel's
    // `useMemo` identity stable in the common case.
    return { ...list, withheldMembers: 0 };
  }
  return {
    ...list,
    entries,
    touchesMergeSet: false,
    // The members are out of scope, so they are not "already current" within
    // it either — the progress modal counts this against its own total.
    currentIds: new Set(
      [...list.currentIds].filter((id) => !memberIds(list).has(id))
    ),
    withheldMembers,
  };
};

/**
 * The ids this list knows to be members.
 *
 * Only the ENTRIES carry `isMember`, so a file that was worked on and needed
 * nothing is not knowable as a member from the list alone. That is why
 * `currentIds` is filtered rather than recomputed: it can only drop the
 * members it can see, and over-counting "already current" by a file that
 * needed nothing anyway costs the modal a number, not a burn.
 */
const memberIds = (list: RedactAllWorkList): ReadonlySet<string> =>
  new Set(
    list.entries.filter((entry) => entry.isMember).map((entry) => entry.tdoId)
  );

/**
 * What the button should say, derived from the list rather than from the
 * surrounding state.
 *
 * SAME SOURCE AS THE ACT. A label computed from "is there a merged document"
 * and a run computed from "which files are behind" are two answers to one
 * question, and they come apart in the case that matters: a request holding
 * stale files on both sides of a merge set would promise a merge the run has
 * no reason to perform, or perform one the label never mentioned. Reading both
 * off the same list is what makes the button honest by construction.
 *
 * The count is the number of files that will be burned — not the number out of
 * date, which includes the ones passed over for never having been worked on.
 */
export type RedactAllButtonState =
  | { readonly kind: 'nothingToDo' }
  | { readonly kind: 'redactAll'; readonly count: number }
  | { readonly kind: 'redactAllAndMerge'; readonly count: number };

export const redactAllButtonState = (
  list: RedactAllWorkList
): RedactAllButtonState => {
  const count = list.entries.length;
  if (count === 0) {
    /**
     * Including the case where every out-of-date file is one nobody has
     * worked on. The press can now OFFER to include those (see
     * `IncludeUnworkedDialog`) — and still does not light up for them:
     * "Redact all" that never goes out is a button permanently lit over a
     * request with nothing to do, which costs the lit state its meaning.
     *
     * The route to that offer is the merged document's own banner, which
     * reports the left-out files and whose Update opens the dialog. This
     * button comes back the moment there is real work beside them, and asks
     * about them then.
     */
    return { kind: 'nothingToDo' };
  }
  return list.touchesMergeSet
    ? { kind: 'redactAllAndMerge', count }
    : { kind: 'redactAll', count };
};
